/**
 * Side-effect file: deeper perf instrumentation.
 *
 * Existing profiler hooks measure the renderer (webgl.render, webgl.frameAll,
 * webgl.handleWorkerMessage). After Phase 6.3 batched chunks shipped, the
 * 1% low FPS was traced to the gap between `frame.time` (~26 ms p99) and
 * `webgl.frameAll` (~11 ms p99) — ~15 ms unaccounted, happening BETWEEN
 * rAF callbacks. Most likely candidate: mineflayer packet decoding on the
 * main thread (NBT chunks, block updates, entity diff).
 *
 * This file adds:
 *   mc.packetDispatch.<name>  per-packet handler time
 *   mc.packetDispatch.total   aggregate per-frame packet processing
 *   mc.packet.size            payload size sample (only when profiler on)
 *
 * Top offenders surface in `__perfDump()` and the metrics beacon. Useful
 * keys after a session: `mc.packetDispatch.chunk_data_and_update_light`,
 * `mc.packetDispatch.block_update`, `mc.packetDispatch.update_attributes`.
 */

import { profiler } from './perfProfiler'

// Browser-native long-task observer. Anything that blocks the main
// thread for >50ms shows up here with its origin attribution. This
// is what catches the missing 22ms between webgl.frameAll p99 and
// frame.time p99 (the gap between rAF callbacks).
// Side-channel for worldDataEmitter.loadChunk to surface column.toJson +
// emit cost via the profiler without importing this module into the
// renderer build (shared with mesher worker).
if (typeof window !== 'undefined' && profiler.enabled) {
  ;(window as any).__perfSampleChunkLoad = (jsonMs: number, emitMs: number, sizeBytes: number) => {
    profiler.sample('mc.loadChunk.toJson', jsonMs)
    profiler.sample('mc.loadChunk.emit', emitMs)
    if (sizeBytes > 0) profiler.sample('mc.loadChunk.jsonBytes', sizeBytes)
  }
  ;(window as any).__perfSampleUpdatePos = (key: string, ms: number) => {
    if (ms > 0.05) profiler.sample(`mc.${key}`, ms)
  }
}

if (typeof window !== 'undefined' && profiler.enabled && typeof PerformanceObserver !== 'undefined') {
  try {
    const obs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const e = entry as any
        // longtask: anything > 50ms on main thread.
        profiler.sample('browser.longtask', entry.duration)
        // Attribution lets us tag where the task originated (script,
        // layout, paint, etc). Keep granularity per attribution name.
        for (const att of (e.attribution ?? [])) {
          const name = att.name || att.containerType || 'unknown'
          profiler.sample(`browser.longtask.${name}`.slice(0, 64), entry.duration)
        }
      }
    })
    obs.observe({ type: 'longtask', buffered: true })
    console.log('[perf] longtask observer active')
  } catch (err) {
    console.warn('[perf] longtask observer failed:', (err as Error).message)
  }

  // Also observe 'event' timing (Interaction-to-Next-Paint sub-events).
  // Tag per event-name so we can distinguish mousemove vs keydown vs
  // pointerdown when input latency bleeds into the 1% lows.
  try {
    const obs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 16) {
          profiler.sample('browser.event.long', entry.duration)
          profiler.sample(`browser.event.${entry.name}`.slice(0, 64), entry.duration)
        }
      }
    })
    obs.observe({ type: 'event', buffered: true, durationThreshold: 16 } as any)
  } catch { /* not all browsers */ }
}

// `customEvents` and `bot` are exposed as globals later in module init.
// We poll for `customEvents` so this side-effect file doesn't crash the
// page if `customEvents` hasn't been assigned yet when this module
// loads (which is what happened on the first attempt). Once available,
// we register the bot-created hook.
if (typeof window !== 'undefined' && profiler.enabled) {
  const tryInstall = () => {
    try {
      const w = window as any
      if (!w.customEvents) return false
      w.customEvents.on('mineflayerBotCreated', () => {
        try {
          const client: any = w.bot?._client
          if (!client || client.__perfWrapped) return
          client.__perfWrapped = true
          const origEmit = client.emit.bind(client)
          client.emit = function (eventName: string, ...args: any[]) {
            if (typeof eventName !== 'string') return origEmit(eventName, ...args)
            const t0 = performance.now()
            const result = origEmit(eventName, ...args)
            const dt = performance.now() - t0
            if (dt > 0.1) {
              profiler.sample(`mc.packetDispatch.${eventName}`, dt)
              profiler.sample('mc.packetDispatch.total', dt)
            }
            return result
          }
          console.log('[perf] mineflayer packet dispatch instrumented')
        } catch (err) {
          console.warn('[perf] could not wrap client.emit:', (err as Error).message)
        }
      })
      return true
    } catch { return false }
  }
  // Poll for ~30s, then give up. Cheap (every 250 ms) and silent if
  // never available (e.g. menu-only session).
  if (!tryInstall()) {
    let tries = 0
    const id = setInterval(() => {
      if (tryInstall() || ++tries > 120) clearInterval(id)
    }, 250)
  }
}
